import { create } from "zustand";

export const LOCATION_SOURCES = { WIFI: "wifi", IP: "ip" };

const initialViewState = {
  locationSource: LOCATION_SOURCES.WIFI,
  selectedMachineId: null,
  summaryMachineId: null,
  checkedMachineIds: [],
  eventOverlayIds: null,
};

/**
 * View state for the full-page case machines map, shared between the toolbar,
 * the sidebar list and the map canvas.
 *
 * Deliberately holds only ids and modes: machine rows stay in the React Query
 * cache so a status change has one source of truth, and filters stay in the URL
 * so they survive a reload and can be shared.
 *
 * Not persisted — unlike the dialer and error stores, none of this should
 * outlive the page or leak into another window.
 */
const useCaseMapStore = create(set => ({
  caseId: null,
  ...initialViewState,

  legendVisible: false,

  // On by default, per the ticket's default state, and deliberately not
  // persisted: the toggle is a per-visit view choice like the legend's.
  proximityRingsVisible: true,

  // Selections belong to the case they were made on, so moving to another
  // case's map starts from scratch rather than highlighting a stale machine.
  resetForCase: caseId =>
    set(state =>
      state.caseId === caseId ? state : { caseId, ...initialViewState }
    ),

  // A filter change is not a case change, so the ids survive it — but a machine
  // the filters excluded must not stay in a bulk selection, where a status change
  // would be written to a machine with no row on screen to show it.
  pruneToVisible: visibleIds =>
    set(state => {
      const next = {};
      const checked = state.checkedMachineIds.filter(id => visibleIds.has(id));
      const overlay = state.eventOverlayIds?.filter(id => visibleIds.has(id));

      if (checked.length !== state.checkedMachineIds.length) {
        next.checkedMachineIds = checked;
      }

      if (overlay && overlay.length !== state.eventOverlayIds.length) {
        // Pruned down to nothing means the overlay is off, not showing zero
        // events — otherwise the toolbar would still offer to hide it.
        next.eventOverlayIds = overlay.length ? overlay : null;
      }

      if (state.selectedMachineId && !visibleIds.has(state.selectedMachineId)) {
        next.selectedMachineId = null;
      }

      if (state.summaryMachineId && !visibleIds.has(state.summaryMachineId)) {
        next.summaryMachineId = null;
      }

      // Same reference when nothing dropped out: zustand treats it as a no-op
      // and skips notifying every sidebar row.
      return Object.keys(next).length ? next : state;
    }),

  setLocationSource: locationSource => set({ locationSource }),

  toggleLegend: () => set(state => ({ legendVisible: !state.legendVisible })),

  toggleProximityRings: () =>
    set(state => ({ proximityRingsVisible: !state.proximityRingsVisible })),

  // Selecting from the sidebar highlights the pin and nothing more: only a pin
  // click may open a summary, so any open one closes here.
  selectMachine: selectedMachineId =>
    set({ selectedMachineId, summaryMachineId: null }),

  // One action so the selection and the summary can never disagree about which
  // machine the map is showing. Clicking the open pin again closes its summary.
  selectFromPin: machineId =>
    set(state => ({
      selectedMachineId: machineId,
      summaryMachineId: state.summaryMachineId === machineId ? null : machineId,
    })),

  closeSummary: () => set({ summaryMachineId: null }),

  toggleChecked: machineId =>
    set(state => ({
      checkedMachineIds: state.checkedMachineIds.includes(machineId)
        ? state.checkedMachineIds.filter(id => id !== machineId)
        : [...state.checkedMachineIds, machineId],
    })),

  clearChecked: () => set({ checkedMachineIds: [] }),

  showEventsFor: eventOverlayIds => set({ eventOverlayIds }),

  dismissEvents: () => set({ eventOverlayIds: null }),
}));

export default useCaseMapStore;
